import React, { useEffect, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import {
  Tabs,
  Snackbar,
  Alert,
} from "@mui/material";

import MainInfo from "./MainInfo/MainInfo";
import {
  getProfile,
} from "../../services/UserService";
import { updateUser } from "../../services/UserService";
import { readUserBookingInterests } from "../../services/BookingInterestService";
import { UserBookingInterest } from "../../models/BookingInterest";
import BookingInterestManagement from "./BookingInterestManagement/BookingInterestManagement";

import { TabPanelBox, PanelContainer, TabsContainer, StyledTab, ProfilePageContainer, HeaderContainer, InfoContainer, TitleTypography, SubTitleTypography } from "./Profile.styles";
import { Avatar, getInitials } from "../../components";
import { Icon } from "../../icons";
import { logoutUser } from "../../utils/auth";
import { useAuthContext } from "src/contexts/AuthContext";


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <TabPanelBox
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <TabPanelBox>{children}</TabPanelBox>}
    </TabPanelBox>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export interface Profile {
  id?: string;
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  intentionalShortName?: boolean | null;
  dateOfBirth: any | null;
  phoneNumber: string | null | undefined;
  emailPreferences: string | undefined;
  accessibility: string | null | undefined;
  otherAccessibility?: string | null;
  gender?: string | null;
  addressId?: string;
  address: {
    id?: string;
    latitude?: number | null;
    longitude?: number | null;
    longLocation?: string | null;
    shortLocation?: string | null;
    detailsJson?: string | null;
    country: string;
    unitOrApartmentNumber?: string | null;
    street: string;
    suburb?: string | null;
    state?: string | null;
    postCode: string;
    createdBy: string;
  };
};

const tabNameToIndex: { [key: string]: number } = {
  'tickets': 0,
  'account': 1,
  'profile': 2, //TODO: Account tab styling + profile Tab
};

const indexToTabName: { [key: number]: string } = {
  0: 'tickets',
  1: 'account',
  2: 'profile',
};

const Profile: React.FC<WithRouterProps> = ({ router }) => {
  const [profile, setProfile] = useState<Profile>();
  const [bookingInterests, setBookingInterests] = useState<UserBookingInterest[]>([]);
  const [snackbarErrorOpen, setSnackbarErrorOpen] = React.useState(false);
  const [snackbarSuccessOpen, setSnackbarSuccessOpen] = React.useState(false);

  const { query } = router;
  const { setUser, user } = useAuthContext();
  const tabParam = query.tab;
  const tab = typeof tabParam === 'string' ? tabParam : '';

  const initialTab = tabNameToIndex[tab.toLowerCase()] || 0;
  const [tabIndex, setTabIndex] = React.useState(initialTab);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarErrorOpen(false);
    setSnackbarSuccessOpen(false);
  };

  const handleMainInfo = async (values: any) => {
    const res = await updateUser({
      ...values,
      id: profile?.id,
      addressId: profile?.addressId,
    });
    if (res) {
      fetchProfile()
      setSnackbarSuccessOpen(true);
    } else {
      setSnackbarErrorOpen(true);
    }
  };

  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    const tabName = indexToTabName[tabIndex];
    router.replace(`/profile${tabName ? `?tab=${tabName}` : ''}`, undefined, { shallow: true, scroll: false });
  }, [tabIndex]);

  useEffect(() => {
    const tabParam = router.query.tab;
    const tab = typeof tabParam === 'string' ? tabParam : '';

    const newTabIndex = tabNameToIndex[tab.toLowerCase()] || 0;

    if (newTabIndex !== tabIndex) {
      setTabIndex(newTabIndex);
    }
  }, [router.query]);

  const fetchProfile = async () => {
    const res = await getProfile();
    if (res && user) {
      setProfile(res);
      setUser({
        ...user,
        email: res.email ?? user.email,
        dateOfBirth: res.dateOfBirth ?? user.dateOfBirth,
        accessibility: res.accessibility ?? '',
        otherAccessibility: res.otherAccessibility ?? '',
        gender: res.gender ?? user.gender,
      });
    }

  };

  const fetchBookingInterests = async () => {
    const { data } = await readUserBookingInterests();
    if (data) setBookingInterests(data);
  };

  const handleLogout = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation;
    logoutUser();
  };

  useEffect(() => {
    fetchProfile();
    fetchBookingInterests();
  }, []);

  useEffect(() => {
    if (profile && user && profile.gender !== user.gender) {
      setProfile({ ...profile, gender: user.gender ?? null });
    }
  }, [user?.gender]);

  return (
    <ProfilePageContainer>
      <TabsContainer>
        <HeaderContainer>
          <Avatar initials={getInitials(profile?.firstName, profile?.lastName)} />
          <InfoContainer>
            <span className="AccountMenu_username">
              {profile?.firstName} {profile?.lastName}
            </span>
            {/* <span>
              Member Score: {memberScore}
            </span> */}
          </InfoContainer>
        </HeaderContainer>
        <Tabs
          value={tabIndex}
          onChange={onTabChange}
          aria-label="profile tabs"
          orientation="vertical"
        >
          <StyledTab icon={<Icon icon="Ticket" />} {...a11yProps(0)} iconPosition="start" label="Applications & Tickets" data-cy="ticketManagement-tab" />
          <StyledTab icon={<Icon icon="User" />} {...a11yProps(1)} iconPosition="start" label="Account Details" data-cy="accountDetails-tab" />
          {/* <StyledTab icon={<Icon icon="Bell" />} {...a11yProps(2)} iconPosition="start" label="Profile" data-cy="profile-tab"/> */}
          <StyledTab onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => handleLogout(event)} icon={<Icon icon="Logout" />} iconPosition="start" label="Logout" data-cy="logout-tab-button" />
        </Tabs>
      </TabsContainer>
      <PanelContainer>
        <TabPanel value={tabIndex} index={0}>
          <TitleTypography>Applications & Tickets</TitleTypography>
          <BookingInterestManagement bookingInterests={bookingInterests} fetchBookingInterests={fetchBookingInterests} />
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          <TitleTypography>Account Details</TitleTypography>
          <SubTitleTypography>Manage your personal and account details</SubTitleTypography>
          <MainInfo handleSubmit={handleMainInfo} profile={profile} fetchProfile={fetchProfile} />
        </TabPanel>
        {/* <TabPanel value={tabIndex} index={2}>
          <TitleTypography>Deactivate Profile</TitleTypography>
          <Account profile={profile} />
        </TabPanel> */}
      </PanelContainer>

      <Snackbar
        open={snackbarErrorOpen}
        autoHideDuration={10000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity="error"
          sx={{ width: "100%", fontSize: "16px" }}
        >
          Save failed!
        </Alert>
      </Snackbar>
      <Snackbar
        open={snackbarSuccessOpen}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          sx={{ width: "100%", fontSize: "16px", marginTop: "70px" }}
        >
          Successfully Saved!
        </Alert>
      </Snackbar>
    </ProfilePageContainer>
  );
};

export default withRouter(Profile);
